
import React, { useState, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { candidateFoldersAPI, candidatesAPI2 } from "@/services/api";
import { Loader2, Folder, FileText, User, X, Download } from "lucide-react";

const EditCandidateModal = ({ isOpen, onClose, candidateId, onSuccess }) => {

  const [loading, setLoading] = useState(false);
  const [folders, setFolders] = useState([]);
  const [loadingFolders, setLoadingFolders] = useState(false);
  const [resumeFile, setResumeFile] = useState(null);
  const [profileImageFile, setProfileImageFile] = useState(null);
  const [profileImagePreview, setProfileImagePreview] = useState(null);
  const [existingResume, setExistingResume] = useState(null);
  const [existingProfileImage, setExistingProfileImage] = useState(null);
  const resumeInputRef = useRef(null);
  const profileImageInputRef = useRef(null);
  const { toast } = useToast();

  const form = useForm({
    defaultValues: {
      name: "",
      email: "",
      mobileNumber: "",
      address: "",
      folderId: "",
    },
  });

  // Fetch candidate data when modal opens
  useEffect(() => {
  if (isOpen) {
    if (candidateId) {
      fetchCandidate();
    }
    fetchFolders();
  }
}, [isOpen, candidateId]); // Add isOpen to dependencies

  const fetchFolders = async () => {
    try {
      setLoadingFolders(true);
      const response = await candidateFoldersAPI.getAll();
      setFolders(response?.data?.data || []);
    } catch (error) {
      console.error("Error fetching folders:", error);
      toast({
        title: "Error",
        description: "Failed to load folders",
        variant: "destructive",
      });
    } finally {
      setLoadingFolders(false);
    }
  };

  const fetchCandidate = async () => {
    setLoading(true);
    try {
      const response = await candidatesAPI2.getOne(candidateId);
      const candidate = response.data;

      // Handle folderId - it could be an object (populated) or string (ObjectId)
      let folderIdValue = "";
      if (candidate.folderId) {
        folderIdValue = typeof candidate.folderId === 'object' 
          ? candidate.folderId._id 
          : candidate.folderId;
      }



      form.reset({
        name: candidate.name || "",
        email: candidate.email || "",
        mobileNumber: candidate.mobileNumber || "",
        address: candidate.address || "",
        folderId: folderIdValue,
      });

      setExistingResume(candidate.resume || null);
      setExistingProfileImage(candidate.profileImage || null);
      setProfileImagePreview(candidate.profileImage || null);

    } catch (error) {
      console.error("Error fetching candidate:", error);
      toast({
        title: "Error",
        description: "Failed to load candidate data",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleResumeChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type !== 'application/pdf') {
        toast({
          title: "Invalid File",
          description: "Only PDF files are allowed for resume",
          variant: "destructive",
        });
        e.target.value = null;
        return;
      }
      setResumeFile(file);
    }
  };

  const handleProfileImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast({
          title: "Invalid File",
          description: "Only image files are allowed for profile picture",
          variant: "destructive",
        });
        e.target.value = null;
        return;
      }
      setProfileImageFile(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfileImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeResume = () => {
    setResumeFile(null);
    if (resumeInputRef.current) {
      resumeInputRef.current.value = null;
    }
  };

  const removeProfileImage = () => {
    setProfileImageFile(null);
    setProfileImagePreview(existingProfileImage);
    if (profileImageInputRef.current) {
      profileImageInputRef.current.value = null;
    }
  };

  const handleSubmit = async (data) => {
    try {
      setLoading(true);

      const formData = new FormData();
      formData.append('name', data.name);
      formData.append('email', data.email);
      formData.append('mobileNumber', data.mobileNumber || '');
      formData.append('address', data.address || '');
      formData.append('folderId', data.folderId || '');
      
      // Append files if new ones are selected
      if (resumeFile) {
        formData.append('resume', resumeFile);
      }
      if (profileImageFile) {
        formData.append('profileImage', profileImageFile);
      }

      const response = await candidatesAPI2.update(candidateId, formData);
      
      toast({
        title: "Success",
        description: "Candidate updated successfully",
      });
      
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Update failed:', error);
      const errorMessage = error.response?.data?.message || "Failed to update candidate";
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[80vw] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Candidate</DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex justify-center items-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : (
          <Form {...form}>
            <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="name"
                rules={{ required: "Name is required" }}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Name</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter name" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="email"
                rules={{
                  required: "Email is required",
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: "Invalid email address"
                  }
                }}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <Input
                        type="email"
                        placeholder="Enter email"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="mobileNumber"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Mobile Number</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter mobile number" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="address"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Address (Optional)</FormLabel>
                    <FormControl>
                      <Textarea 
                        placeholder="Enter address" 
                        className="resize-none"
                        rows={3}
                        {...field} 
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
              control={form.control}
              name="folderId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Folder (Optional)</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value || ""}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a folder or leave empty for Others" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {loadingFolders ? (
                        <SelectItem value="loading" disabled>
                          Loading folders...
                        </SelectItem>
                      ) : (
                        folders.map((folder) => (
                          <SelectItem key={folder._id} value={folder._id}>
                            <div className="flex items-center">
                              <div 
                                className="mr-2 h-3 w-3 rounded-full"
                                style={{ backgroundColor: folder.color }}
                              />
                              {folder.name}
                            </div>
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Resume Upload */}
            <div className="space-y-2">
              <FormLabel className="flex items-center gap-2">
                <FileText className="h-4 w-4" />
                Resume
              </FormLabel>
              {existingResume && !resumeFile && (
                <div className="flex items-center gap-2 p-2 bg-gray-50 rounded">
                  <FileText className="h-4 w-4 text-blue-600" />
                  <span className="text-sm flex-1">Current resume uploaded</span>
                  <a 
                    href={existingResume} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800"
                  >
                    <Download className="h-4 w-4" />
                  </a>
                </div>
              )}
              <FormDescription className="text-xs">
                Upload a new resume to replace the existing one (PDF only)
              </FormDescription>
              <div className="flex items-center gap-2">
                <Input
                  ref={resumeInputRef}
                  type="file"
                  accept=".pdf,application/pdf"
                  onChange={handleResumeChange}
                  className="flex-1"
                />
                {resumeFile && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={removeResume}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
              {resumeFile && (
                <p className="text-sm text-green-600 flex items-center gap-1">
                  <FileText className="h-4 w-4" />
                  New: {resumeFile.name}
                </p>
              )}
            </div>

            {/* Profile Picture Upload */}
            <div className="space-y-2">
              <FormLabel className="flex items-center gap-2">
                <User className="h-4 w-4" />
                Profile Picture (Optional)
              </FormLabel>
              {profileImagePreview && (
                <div className="mt-2">
                  <img
                    src={profileImagePreview}
                    alt="Profile preview"
                    className="h-20 w-20 rounded-full object-cover border-2 border-gray-200"
                  />
                </div>
              )}
              <FormDescription className="text-xs">
                Upload a new profile picture (JPG, PNG, etc.)
              </FormDescription>
              <div className="flex items-center gap-2">
                <Input
                  ref={profileImageInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleProfileImageChange}
                  className="flex-1"
                />
                {profileImageFile && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={removeProfileImage}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
              {profileImageFile && (
                <p className="text-sm text-green-600 flex items-center gap-1">
                  <User className="h-4 w-4" />
                  New image selected
                </p>
              )}
            </div>

              <DialogFooter className="pt-4">
                <Button variant="outline" onClick={onClose} type="button">
                  Cancel
                </Button>
                <Button type="submit" disabled={loading}>
                  {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Save Changes
                </Button>
              </DialogFooter>
            </form>
          </Form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default EditCandidateModal;